import { Combobox, Transition } from '@headlessui/react';
import { CheckIcon, ChevronUpDownIcon, NoSymbolIcon } from '@heroicons/react/20/solid';
import classNames from 'classnames';
import { Fragment, useEffect, useState } from 'react';
import { getAllUf } from '../app.service';
import { useApp } from '../AppContext';
import { IComboboxData } from './ComboboxComp';
import LoadingComp from './LoadingComp';

interface Props {
  value: string | undefined;
  onChange: (value: string) => void;
  disabled?: boolean;
  min?: boolean;
}

export default function UfComboboxComp({ value, onChange, disabled = false, min = false }: Props) {
  const { ufs } = useApp();
  const [data, setData] = useState<IComboboxData[]>([]);
  const [selected, setSelected] = useState<IComboboxData>();
  const [query, setQuery] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    setLoading(true);

    const map = ufs.map((d) => ({
      id: d.uf,
      desc: d.descricao,
      isVisible: true,
    }));
    setData(map);
    setLoading(false);
  }, [ufs]);

  useEffect(() => {
    if (selected) onChange(selected.id);
  }, [onChange, selected]);

  useEffect(() => {
    if (value && data) {
      setSelected(data.find((d) => d.id === value));
    }
  }, [data, value]);

  const filtered =
    query === ''
      ? data
      : data
          .filter((dt) =>
            dt.desc
              .toLowerCase()
              .replace(/\s+/g, '')
              .includes(query.toLowerCase().replace(/\s+/g, '')),
          )
          .filter((d) => d.isVisible);

  if (loading) {
    return (
      <div className="w-full flex justify-start form-input rounded-lg focus:ring-0">
        <LoadingComp />
      </div>
    );
  }

  return (
    <div className="inline-flex w-full z-0">
      <Combobox value={selected} onChange={setSelected} disabled={disabled}>
        <div className="relative w-full">
          <div className="relative w-full cursor-default overflow-hidden rounded-lg bg-white text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-opacity-75 focus-visible:ring-offset-2 focus-visible:ring-offset-primary/20 sm:text-sm">
            <Combobox.Input
              className={classNames(
                'w-full rounded-lg pl-3 pr-10 text-md text-gray-900 focus:ring-0',
                min && 'text-xs',
              )}
              onChange={(event) => setQuery(event.target.value)}
              // eslint-disable-next-line @typescript-eslint/no-explicit-any
              displayValue={(dt: any) => dt?.desc || ''}
              onInput={() => setQuery('')}
              onFocus={(event: any) => event.target.select()}
            />
            <Combobox.Button className="absolute inset-y-0 right-0 flex items-center pr-2">
              <ChevronUpDownIcon className="h-5 w-5 text-gray-400" aria-hidden="true" />
            </Combobox.Button>
          </div>
          <Transition
            as={Fragment}
            leave="transition ease-in duration-100"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
            afterLeave={() => setQuery('')}
          >
            <Combobox.Options className="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 text-base ring-1 ring-black ring-opacity-5 focus:outline-none sm:text-sm">
              {filtered.length === 0 && query !== '' ? (
                <div className="relative cursor-default select-none py-1 px-4 text-gray-700">
                  Não encontrado.
                </div>
              ) : (
                filtered.map((data) => (
                  <Combobox.Option
                    key={data.id}
                    className={({ active }) =>
                      `relative cursor-default py-2 pl-10 pr-4 ${
                        active && 'bg-primary text-white'
                      } ${
                        data.isVisible
                          ? 'cursor-default'
                          : 'cursor-not-allowed bg-red-50 text-red-400'
                      }`
                    }
                    value={data}
                    disabled={!data.isVisible}
                  >
                    {({ selected, active }) => (
                      <div className="">
                        <span
                          className={`block truncate ${selected ? 'font-medium' : 'font-normal'}`}
                        >
                          {data.desc}
                        </span>
                        {selected ? (
                          <span
                            className={`absolute inset-y-0 left-0 flex items-center pl-3 ${
                              active ? 'text-white' : 'text-current'
                            }`}
                          >
                            <CheckIcon className="h-5 w-5" aria-hidden="true" />
                          </span>
                        ) : data.isVisible ? null : (
                          <span
                            className={
                              'absolute inset-y-0 left-0 flex items-center pl-3 text-red-300'
                            }
                          >
                            <NoSymbolIcon className="h-5 w-5" aria-hidden="true" />
                          </span>
                        )}
                      </div>
                    )}
                  </Combobox.Option>
                ))
              )}
            </Combobox.Options>
          </Transition>
        </div>
      </Combobox>
    </div>
  );
}
